<template>
  <div v-if="modelValue" class="side-panel-background">
    <div class="side-panel">
      <div class="side-panel-header" :class="{ 'side-panel-header--overflow': y > 0 && !arrivedState.top }">
        <slot name="header"> </slot>
      </div>
      <div ref="body" class="side-panel-body">
        <slot />
      </div>

      <div
        v-if="!hideButtons"
        class="side-panel-footer"
        :class="{ 'side-panel-footer--overflow': y >= 0 && !arrivedState.bottom }"
      >
        <slot name="footer">
          <UIButton id="side-panel-close" class="side-panel-footer__button" variant="secondary" @click="handleClose">
            <slot name="footer-button-close">Close</slot>
          </UIButton>
          <UIButton
            id="side-panel-apply"
            class="side-panel-footer__button side-panel-footer__button--apply"
            :loading="loading"
            :disabled="disabled"
            @click="emit('apply')"
          >
            <slot name="footer-button-apply">Apply</slot>
          </UIButton>
        </slot>
      </div>

      <div v-if="!hideCloseButton" class="side-panel-close" @click="handleClose">
        <UIIcon name="big-close" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useBodyScrollLock from '../../composables/useBodyScrollLock'

const modelValue = defineModel<boolean>()

defineProps<{
  loading?: boolean
  disabled?: boolean
  hideButtons?: boolean
  hideCloseButton?: boolean
}>()

const emit = defineEmits<{
  (e: 'apply'): void
  (e: 'close'): void
}>()

const { isMobile } = useAppBreakpoints()

const body = ref<HTMLDivElement | null>(null)

const { y, arrivedState } = useScroll(body)

const { lock, unlock } = useBodyScrollLock()

const handleClose = () => {
  emit('close')
  unlock()
}

watch(() => modelValue.value, (newValue) => {
  if (newValue) {
    lock()
  } else {
    unlock()
  }
})

onMounted(() => {
  if (modelValue.value) {
    lock()
  }
})

onUnmounted(() => {
  unlock()
})

onKeyStroke('Escape', () => {
  handleClose()
})
</script>

<style scoped lang="scss">
@use '@/styles/mixins.scss' as *;
@use '@/styles/variables.scss' as *;

$side-panel-padding: 24px;
$close-size: 40px;
$side-panel-header-height: 72px;
$side-panel-footer-height: 112px;
$side-panel-gap: 24px;

.side-panel {
  display: flex;
  flex-direction: column;
  position: fixed;
  background: white;
  box-shadow: $base-box-shadow;
  z-index: 60;

  @media screen and (max-width: 1024px) {
    right: 0;
    top: 0;
    border-radius: 0;
    width: 100vw;
    height: 100vh;
    min-height: none;
    min-width: none;
    max-width: none;
  }

  @media screen and (min-width: 1024px) {
    top: $side-panel-padding;
    left: calc(100vw - 477px - $side-panel-padding);
    bottom: $side-panel-padding;
    min-height: calc(100dvh - $side-panel-padding * 2);
    max-width: 477px;
    width: 100%;
    border-radius: 24px;
    padding-bottom: $side-panel-footer-height;
  }

  &-header {
    @include headline4();
    padding-left: 24px;
    padding-top: 24px;
    padding-bottom: 8px;
    position: relative;

    &::before {
      content: '';
      display: block;
      position: absolute;
      bottom: -24px;
      right: 32px;
      height: 24px;
      width: calc(100% - 64px);
      opacity: 0;
      visibility: hidden;
      transition: $base-transition;
      @include baseShadow(180deg);
    }

    &--overflow {
      &::before {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &-body {
    display: flex;
    flex-direction: column;
    position: relative;
    flex-grow: 1;
    overflow-y: auto;
    max-height: none;
    @media screen and (max-width: 1023px) {
      padding-bottom: 72px;
    }
    @media screen and (min-width: 1024px) {
      max-height: calc(
        100dvh - #{$side-panel-header-height} - #{$side-panel-footer-height} - #{$side-panel-padding * 2} - #{$side-panel-gap}
      );
    }
    @include baseScrollbar();
  }

  &-footer {
    padding: 24px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    height: $side-panel-footer-height;
    gap: 16px;
    position: absolute;
    bottom: 0;
    width: 100%;
    left: 0;
    background: white;
    border-radius: 0 0 24px 24px;

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: -24px;
      right: 32px;
      height: 24px;
      width: calc(100% - 64px);
      opacity: 0;
      visibility: hidden;
      transition: $base-transition;
      @include baseShadow(0deg);
    }

    &--overflow {
      &::before {
        opacity: 1;
        visibility: visible;
      }
    }

    &__button {
      &--apply {
        min-width: 68px;
      }
    }
  }

  &-close {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    box-shadow: $base-box-shadow;
    cursor: pointer;
    position: absolute;
    top: $side-panel-padding;
    left: calc(-#{$close-size} - $side-panel-padding);
    @media screen and (max-width: 1024px) {
      top: 20px !important;
      right: 20px !important;
      left: auto;
      box-shadow: none;
    }
  }

  &-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.25);
    z-index: 21;
    backdrop-filter: blur(3px);
  }
}
</style>
